import React from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import { bindActionCreators, compose } from 'redux'
import { connect } from 'react-redux'
import Icon from '../general/Icon'
import translate from '../../helper/translate'
import { updateUserSetting } from '../../state/actionCreators/app'
import { desktop, mobile } from '../../styles/media'
import Tooltip from '../general/Tooltip'

const ShowOwnershipToggle = ({ cardSearch, cardSearchFullscreen, deckbuilder, label, maxWidth, onShowOwnership, settings, small, t, updateUserSetting }) => {
	const key = 'collection.showOwnership'
	const title = t('general.showOwnership')
	const showOwnership = settings[key]

	return (
		<Tooltip title={!label ? title : null}>
			<StyledShowOwnershipToggle
				onClick={() => {
					const newValue = !showOwnership

					updateUserSetting(key, newValue, true)

					if (onShowOwnership && newValue) {
						onShowOwnership()
					}
				}}
				active={showOwnership}
				deckbuilder={deckbuilder}
				cardSearch={cardSearch}
				cardSearchFullscreen={cardSearchFullscreen}
			>
				<ShowOwnershipStatus
					active={showOwnership}
					deckbuilder={deckbuilder}
					cardSearch={cardSearch}
					cardSearchFullscreen={cardSearchFullscreen}
					small={small}
				>
					<Icon name="archive" />
					<Eye
						cardSearch={cardSearch}
						cardSearchFullscreen={cardSearchFullscreen}
						small={small}
					>
						<Icon name={showOwnership ? 'eye' : 'eye-off'} />
					</Eye>
				</ShowOwnershipStatus>
				{label && (
					<Label
						active={showOwnership}
						cardSearchFullscreen={cardSearchFullscreen}
						maxWidth={maxWidth}
						small={small}
					>
						{title}
					</Label>
				)}
			</StyledShowOwnershipToggle>
		</Tooltip>
	)
}

ShowOwnershipToggle.propTypes = {
	cardSearch: PropTypes.bool,
	cardSearchFullscreen: PropTypes.bool,
	deckbuilder: PropTypes.bool,
	label: PropTypes.bool,
	maxWidth: PropTypes.string,
	onShowOwnership: PropTypes.func,
	settings: PropTypes.object.isRequired,
	small: PropTypes.bool,
	t: PropTypes.func.isRequired,
	updateUserSetting: PropTypes.func.isRequired
}

ShowOwnershipToggle.defaultProps = {
	cardSearch: false,
	cardSearchFullscreen: false,
	deckbuilder: false,
	label: false,
	maxWidth: null,
	onShowOwnership: null,
	small: false
}

const ShowOwnershipStatus = styled.div`
	background: ${props => props.theme.background};
	border: 1px solid ${props => props.theme.background};
	box-shadow: 0 0 0 2px ${props => props.theme.backgroundLight};
	border-radius: 8px;
	position: relative;
	transition: 0.1s;

	height: 34px;
	width: 34px;
	min-width: 34px;

	align-items: center;
	display: flex;
	justify-content: center;

	color: ${props => props.theme.textLight};

	> svg {
		height: 24px;
		width: 24px;
	}

	${props => props.small && `
		border-radius: 6px;
		height: 26px;
		width: 26px;
		min-width: 26px;

		> svg {
			height: 16px;
			width: 16px;
		}
	`}

	${props => props.cardSearch && `
		background: #444;
		border: 0;
		border-radius: 2px;
		box-shadow: none;
		color: #999;
		margin-left: 0.5rem;
		height: 40px;
		width: 40px;
	`}

	${props => props.cardSearchFullscreen && `
		background: transparent;
		border: 0;
		border-radius: 2px;
		box-shadow: none;
		height: 22px;
		width: 22px;
		min-width: 22px;
		margin-left: 0.5rem;

		> svg {
			height: 18px;
			width: 18px;
		}
	`}

	${props => props.deckbuilder && `
		box-shadow: none;
		color: ${props.theme.textVeryLight};
		height: 36px;
		width: 36px;
		min-width: 36px;

		&, ${Eye} {
			background: ${props.theme.backgroundLight};
		}
	`}

	${props => props.active && `
		box-shadow: 0 0 0 2px ${props.theme.primary};
		color: ${props.theme.text};

		${Eye} svg {
			color: ${props.theme.textLight};
		}

		${(props.cardSearch || props.cardSearchFullscreen) ? `
			&, ${Eye} svg {
				color: #EEE;
			}
		` : ''}

		${props.deckbuilder && `
			box-shadow: none;
			border-color: ${props.theme.primary};
			color: ${props.theme.white};

			&, ${Eye} {
				background: ${props.theme.primary};
			}

			${Eye} svg {
				color: #EEE;
			}
		`}
	`}
`

const Eye = styled.div`
	background: ${props => props.theme.background};
	border-radius: 50%;
	transition: 0.1s;

	height: 14px;
	width: 14px;

	position: absolute;
	top: auto;
	bottom: 0;
	right: 0;
	z-index: 5;

	align-items: center;
	display: flex;
	justify-content: center;

	svg {
		color: ${props => props.theme.textVeryLight};

		height: 10px;
		width: 10px;
	}

	${props => props.cardSearch && `
		background: #444;
		bottom: 4px;
		right: 4px;

		svg {
			color: #EEE;
		}
	`}

	${props => props.small && `
		height: 12px;
		width: 12px;

		svg {
			height: 8px;
			width: 8px;
		}
	`}

	${props => props.cardSearchFullscreen && `
		background: #1F1F1F;

		svg {
			color: #EEE;
		}
	`}
`

const Label = styled.div`
	color: ${props => props.theme.textLight};
	font-size: 0.9rem;
	margin-left: 0.75rem;
	transition: 0.1s;

	${props => props.active && `
		color: ${props.theme.text};
		font-weight: 500;
	`}

	${props => props.small && `
		font-size: 0.8rem;
	`}

	${props => props.cardSearchFullscreen && `
		color: ${props.active ? props.theme.white : 'rgba(255, 255, 255, 0.7)'};
	`}

	${mobile`
		font-size: 0.8rem;
		white-space: nowrap;

		${props => props.maxWidth && `
			text-overflow: ellipsis;
			overflow: hidden;
			max-width: ${props.maxWidth};
		`}
	`}
`

const StyledShowOwnershipToggle = styled.button`
	background: transparent;
	border: 0;
	padding: 0;

	align-items: center;
	display: flex;
	cursor: pointer;

	${props => props.deckbuilder && `
		cursor: default;
	`}

	${desktop`
		&:hover {
			${props => !props.active && `
				${ShowOwnershipStatus} {
					box-shadow: 0 0 0 2px ${props.theme.backgroundSolid};

					${props.deckbuilder ? `
						box-shadow: none;
						color: ${props.theme.textLight}
					` : ''}

					${props.cardSearch ? `
						background: #666;
						box-shadow: none;
						color: #EEE;
					` : ''}
				}

				${Label} {
					color: ${props.theme.text};
				}

				${props.cardSearch ? `
					${Eye} {
						background: #666;
					}
				` : ''}

				${props.cardSearchFullscreen ? `
					${Label} {
						color: rgba(255, 255, 255, 1) !important;
					}
				` : ''}
			`}
		}
	`}
`

const enhance = compose(
	connect(state => ({
		settings: state.persist.user.settings
	}), dispatch => bindActionCreators({
		updateUserSetting
	}, dispatch)),
	translate('general')
)

export default enhance(ShowOwnershipToggle)
